/**
 * Utility functions for formatting units and currencies
 */ const BYTE_UNITS = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB",
    "PB"
];
const toFormattedBytesParts = (bytes, decimals)=>{
    if (bytes === 0) {
        return {
            value: 0,
            unit: "B"
        };
    }
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const i = Math.min(Math.max(Math.floor(Math.log(bytes) / Math.log(k)), 0), BYTE_UNITS.length - 1);
    return {
        value: parseFloat((bytes / Math.pow(k, i)).toFixed(dm)),
        unit: BYTE_UNITS[i]
    };
};
/**
 * Format bytes to human-readable units
 * @param bytes - The number of bytes
 * @returns Formatted string with appropriate unit (B, KB, MB, GB, TB)
 */ export const formatBytes = (bytes)=>{
    const { value, unit } = toFormattedBytesParts(bytes, 0);
    return `${value}${unit}`;
};
/**
 * Format bytes with more precision
 * @param bytes - The number of bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with appropriate unit
 */ export const formatBytesPrecise = (bytes, decimals = 2)=>{
    const { value, unit } = toFormattedBytesParts(bytes, decimals);
    return `${value} ${unit}`;
};
/** Returns true only when the value is in raw bytes and needs auto-scaling */ export const isByteUnit = (unit)=>{
    if (!unit) return false;
    const normalizedUnit = unit.toUpperCase();
    return normalizedUnit === "BYTES" || normalizedUnit === "BYTE" || normalizedUnit === "B";
};
export const formatUsageValue = (value, unit)=>{
    if (!unit) return formatBytesPrecise(value);
    return isByteUnit(unit) ? formatBytesPrecise(value) : `${value.toLocaleString()} ${unit}`;
};
/**
 * Format value with unit of measure
 * @param value - The numeric value
 * @param unitOfMeasure - The unit of measure
 * @returns Formatted string with appropriate unit
 */ export const formatWithUnit = (value, unitOfMeasure)=>{
    if (!unitOfMeasure) return value.toLocaleString();
    const unit = unitOfMeasure.toLowerCase();
    // Handle byte-based units
    if (unit.includes("byte")) {
        return formatBytes(value);
    }
    // Handle specific units
    switch(unit){
        case "mb":
        case "megabytes":
            return `${value.toLocaleString()} MB`;
        case "gb":
        case "gigabytes":
            return `${value.toLocaleString()} GB`;
        case "tb":
        case "terabytes":
            return `${value.toLocaleString()} TB`;
        case "requests":
        case "calls":
            return `${value.toLocaleString()} ${unit}`;
        default:
            return `${value.toLocaleString()} ${unitOfMeasure}`;
    }
};
/**
 * Format tier range with appropriate units
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (exclusive), null for unbounded
 * @param unitOfMeasure - The unit of measure
 * @returns Formatted range string
 */ export const formatTierRange = (min, max, unitOfMeasure)=>{
    const unit = unitOfMeasure?.toLowerCase() || "bytes";
    // For byte-based units, use byte formatting
    if (unit.includes("byte")) {
        if (max === null || max === undefined) {
            return `${formatBytes(min)}+`;
        }
        return `${formatBytes(min)} - ${formatBytes(max)}`;
    }
    // For other units, show numbers with unit
    if (max === null || max === undefined) {
        return `${min.toLocaleString()}+ ${unitOfMeasure}`;
    }
    return `${min.toLocaleString()} - ${max.toLocaleString()} ${unitOfMeasure}`;
};
/**
 * Get currency symbol from currency code
 * @param currencyCode - The currency code (USD, EUR, GBP, etc.)
 * @returns The currency symbol
 */ export const getCurrencySymbol = (currencyCode)=>{
    const symbols = {
        USD: "$",
        EUR: "€",
        GBP: "£",
        JPY: "¥",
        CNY: "¥",
        INR: "₹",
        CAD: "$",
        AUD: "$",
        CHF: "Fr"
    };
    return symbols[currencyCode?.toUpperCase()] || currencyCode || "$";
};
/**
 * Format currency amount
 * @param amount - The numeric amount
 * @param currencyCode - The currency code
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted currency string
 */ export const formatCurrency = (amount, currencyCode = "USD", locale = "en-US")=>{
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: currencyCode
        }).format(amount);
    } catch  {
        // Fallback if currency code is not valid
        const symbol = getCurrencySymbol(currencyCode);
        return `${symbol}${amount.toFixed(2)}`;
    }
};
/**
 * Aggregate paid / pending / overdue totals from a list of invoices.
 */ export function calculateInvoiceTotals(invoices) {
    return {
        totalPaid: invoices.filter((inv)=>inv.status === "PAID").reduce((sum, inv)=>sum + inv.amountPaid, 0),
        totalPending: invoices.filter((inv)=>inv.status === "PENDING").reduce((sum, inv)=>sum + inv.amountDue, 0),
        totalOverdue: invoices.filter((inv)=>inv.status === "OVERDUE").reduce((sum, inv)=>sum + inv.balance, 0)
    };
}
